import React, { useMemo, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Button, Divider, Form, Message, Modal } from "semantic-ui-react";

import { asMutation } from "simplydo/core/query";
import { Input } from "simplydo/react/component-library";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import AiWarning from "components/lib/AiWarning";
import { DateInput } from "components/lib/DateInputs";
import { TooltipButton } from "components/lib/UI";

type IPlanConfiguration = {
  from: Date;
  to: Date;
  name: string;
  channels: Array<{ channel: string; frequency: string; count: number }>;
  tone: IToneStrings;
};

type IToneStrings = "smartCasual" | "formal" | "auto";

const GeneratePlanModal = ({
  onClose,
  challengeId,
  generatePlanModalOpen,
  calendar,
  getCalendar,
}) => {
  const user = useAppSelector((state) => state.user);
  const getDefaultDates = (calendar: {
    start?: string | Date;
    end?: string | Date;
  }) => {
    const from = calendar.start ? new Date(calendar.start) : new Date();

    const nextMonth = new Date(from);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const to = calendar.end ? new Date(calendar.end) : nextMonth;

    return { from, to };
  };

  const { from, to } = useMemo(() => getDefaultDates(calendar), [calendar]);

  const [planConfiguration, setPlanConfiguration] =
    useState<IPlanConfiguration>({
      from: from,
      to: to,
      channels: [],
      tone: "smartCasual",
      name: "My engagement plan",
    });

  const generatePlanMutation = useMutation({
    mutationKey: ["challenges", challengeId, "calendar", "plans"],
    mutationFn: asMutation(api.challenges.generatePlan),
    onSuccess: () => {
      getCalendar();
      onClose();
      setPlanConfiguration({
        from: from,
        to: to,
        channels: [],
        tone: "smartCasual",
        name: "My engagement plan",
      });
    },
  });

  return (
    <Modal
      open={generatePlanModalOpen}
      onClose={onClose}
      size="small"
      mountNode={document.getElementById("semantic-modal-mount-node")}
    >
      <Modal.Header>Generate a new engagement plan</Modal.Header>
      <Modal.Content scrolling>
        <h5>Step 1: Configure your plan</h5>
        <Form>
          <Form.Field
            control={Input}
            onChange={(e) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            label="Name your plan"
            placeholder="My engagement plan"
            value={planConfiguration?.name}
          />
          <h5>
            I want to generate engagement activity to take place between these
            dates
          </h5>
          <Form.Group label="I want to generate engagement activity to take place between these dates">
            <Form.Field>
              <label>Start:</label>
              <DateInput
                key="from"
                dateFormat="yyyy-MM-dd"
                value={new Date(planConfiguration.from)}
                onChange={(_e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    from: new Date(value),
                  }))
                }
              />
            </Form.Field>
            <Form.Field>
              <label>End:</label>
              <DateInput
                key="end"
                dateFormat="yyyy-MM-dd"
                value={new Date(planConfiguration.to)}
                onChange={(_e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    to: new Date(value),
                  }))
                }
              />
            </Form.Field>
          </Form.Group>
          <h5>Choose which engagement channels to use</h5>
          <Form.Checkbox
            label="Direct email"
            checked={
              !!planConfiguration?.channels?.find((c) => c.channel === "email")
            }
            onChange={(_e, { checked }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                channels:
                  checked ?
                    [
                      ...(prev.channels || []),
                      { channel: "email", frequency: "monthly", count: 1 },
                    ]
                  : prev.channels?.filter((c) => c.channel !== "email"),
              }))
            }
          />
          {!!planConfiguration?.channels?.find(
            (c) => c.channel === "email",
          ) && (
            <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
              Schedule
              <Form.Field
                control={Input}
                size="small"
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "email",
                  )?.count
                }
                onChange={(e) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "email" ?
                        { ...c, count: Number(e.target.value) }
                      : c,
                    ),
                  }))
                }
                type="number"
                style={{
                  maxWidth: 70,
                  marginRight: 8,
                  marginLeft: 8,
                  marginBottom: -7,
                }}
              />
              per
              <Form.Select
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "email",
                  )?.frequency || ""
                }
                style={{
                  marginRight: 8,
                  marginLeft: 8,
                  maxWidth: "fit-content",
                  marginTop: -6,
                }}
                options={[
                  { value: "weekly", text: "Week" },
                  { value: "fortnightly", text: "Fortnight" },
                  { value: "monthly", text: "Month" },
                ]}
                onChange={(_e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "email" ?
                        { ...c, frequency: value as string }
                      : c,
                    ),
                  }))
                }
              />
            </div>
          )}
          {(util.hasPermission(
            user,
            "challenge.manageAnnouncements",
            challengeId,
          ) ||
            util.hasPermission(
              user,
              "org.manageChallenges",
              user.organisation,
            )) && (
            <Form.Checkbox
              label={`${util.appName()} announcement to audience`}
              checked={
                !!planConfiguration?.channels?.find(
                  (c) => c.channel === "announcement",
                )
              }
              onChange={(_e, { checked }) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels:
                    checked ?
                      [
                        ...(prev.channels || []),
                        {
                          channel: "announcement",
                          frequency: "monthly",
                          count: 1,
                        },
                      ]
                    : prev.channels?.filter(
                        (c) => c.channel !== "announcement",
                      ),
                }))
              }
            />
          )}
          {!!planConfiguration?.channels?.find(
            (c) => c.channel === "announcement",
          ) && (
            <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
              Schedule
              <Form.Field
                control={Input}
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "announcement",
                  ).count
                }
                onChange={(e) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "announcement" ?
                        { ...c, count: Number(e.target.value) }
                      : c,
                    ),
                  }))
                }
                size="small"
                type="number"
                style={{
                  maxWidth: 70,
                  marginRight: 8,
                  marginLeft: 8,
                  marginBottom: -7,
                }}
              />
              per
              <Form.Select
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "announcement",
                  ).frequency || ""
                }
                style={{
                  marginRight: 8,
                  marginLeft: 8,
                  maxWidth: "fit-content",
                  marginTop: -6,
                }}
                options={[
                  { value: "weekly", text: "Week" },
                  { value: "fortnightly", text: "Fortnight" },
                  { value: "monthly", text: "Month" },
                ]}
                onChange={(e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "announcement" ?
                        { ...c, frequency: value as string }
                      : c,
                    ),
                  }))
                }
              />
            </div>
          )}
          <Form.Checkbox
            label="Teams message"
            checked={
              !!planConfiguration?.channels?.find((c) => c.channel === "teams")
            }
            onChange={(_e, { checked }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                channels:
                  checked ?
                    [
                      ...(prev.channels || []),
                      { channel: "teams", frequency: "monthly", count: 1 },
                    ]
                  : prev.channels?.filter((c) => c.channel !== "teams"),
              }))
            }
          />
          {!!planConfiguration?.channels?.find(
            (c) => c.channel === "teams",
          ) && (
            <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
              Schedule
              <Form.Field
                control={Input}
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "teams",
                  ).count
                }
                onChange={(e) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "teams" ?
                        { ...c, count: Number(e.target.value) }
                      : c,
                    ),
                  }))
                }
                size="small"
                type="number"
                style={{
                  maxWidth: 70,
                  marginRight: 8,
                  marginLeft: 8,
                  marginBottom: -7,
                }}
              />
              per
              <Form.Select
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "teams",
                  ).frequency || ""
                }
                style={{
                  marginRight: 8,
                  marginLeft: 8,
                  maxWidth: "fit-content",
                  marginTop: -6,
                }}
                options={[
                  { value: "weekly", text: "Week" },
                  { value: "fortnightly", text: "Fortnight" },
                  { value: "monthly", text: "Month" },
                ]}
                onChange={(e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "teams" ?
                        { ...c, frequency: value as string }
                      : c,
                    ),
                  }))
                }
              />
            </div>
          )}
          <Form.Checkbox
            label="Social post"
            checked={
              !!planConfiguration?.channels?.find((c) => c.channel === "social")
            }
            onChange={(_e, { checked }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                channels:
                  checked ?
                    [
                      ...(prev.channels || []),
                      { channel: "social", frequency: "monthly", count: 1 },
                    ]
                  : prev.channels?.filter((c) => c.channel !== "social"),
              }))
            }
          />
          {planConfiguration?.channels?.find((c) => c.channel === "social") && (
            <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
              Schedule
              <Form.Field
                control={Input}
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "social",
                  ).count
                }
                onChange={(e) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "social" ?
                        { ...c, count: Number(e.target.value) }
                      : c,
                    ),
                  }))
                }
                size="small"
                type="number"
                style={{
                  maxWidth: 70,
                  marginRight: 8,
                  marginLeft: 8,
                  marginBottom: -7,
                }}
              />
              per
              <Form.Select
                value={
                  planConfiguration?.channels?.find(
                    (c) => c.channel === "social",
                  ).frequency || ""
                }
                style={{
                  marginRight: 8,
                  marginLeft: 8,
                  maxWidth: "fit-content",
                  marginTop: -6,
                }}
                options={[
                  { value: "weekly", text: "Week" },
                  { value: "fortnightly", text: "Fortnight" },
                  { value: "monthly", text: "Month" },
                ]}
                onChange={(e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    channels: prev.channels?.map((c) =>
                      c.channel === "social" ?
                        { ...c, frequency: value as string }
                      : c,
                    ),
                  }))
                }
              />
            </div>
          )}
          <h5>Choose a tone-of-voice</h5>
          <Form.Radio
            label="Smart-casual"
            value="smartCasual"
            checked={planConfiguration?.tone === "smartCasual"}
            onChange={(_e, { value }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                tone: value as IToneStrings,
              }))
            }
          />
          <Form.Radio
            label="Formal"
            value="formal"
            checked={planConfiguration?.tone === "formal"}
            onChange={(_e, { value }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                tone: value as IToneStrings,
              }))
            }
          />
          <Form.Radio
            label="Auto-detect"
            value="auto"
            checked={planConfiguration?.tone === "auto"}
            onChange={(_e, { value }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                tone: value as IToneStrings,
              }))
            }
          />
        </Form>
        <Divider hidden />
        <Message style={{ background: "#f7f7f7", color: "#555" }}>
          You&apos;ll be able to update your engagement plan at any time.
        </Message>
        <AiWarning />
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <TooltipButton
          tooltip={
            (!planConfiguration?.name ||
              !planConfiguration?.channels?.length) &&
            "Please ensure you have filled out the form before continuing."
          }
          primary
          onClick={() =>
            generatePlanMutation.mutate([challengeId, planConfiguration])
          }
          loading={generatePlanMutation.isPending}
          disabled={
            generatePlanMutation.isPending ||
            !planConfiguration?.name ||
            !planConfiguration?.channels?.length
          }
        >
          Finish
        </TooltipButton>
      </Modal.Actions>
    </Modal>
  );
};

export default GeneratePlanModal;
